import React, { useState } from 'react';
import './ProductsTable.css'; // External CSS for clean separation

/** Must match nebulapi billingInvoice INTEGRATION_PRODUCT_STATS_SUFFIX and InvoiceDashboard.js */
const INTEGRATION_PRODUCT_STATS_SUFFIX = ' (Integration)';

const ProductsTable = ({ data, products, loading }) => {
  const [expandedSections, setExpandedSections] = useState({
    subscription: true,
    apiIntegration: true,
    others: true
  });
  const toggleSection = section => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  // Helper function to find value by title in the data array
  const findValueByTitle = title => {
    if (loading) return <div className="spinner"></div>;
    if (!data || !Array.isArray(data)) return '';
    const item = data.find(item => item.title === title);
    if (!item) return '';

    const value = item.value;
    if (typeof value === 'number' || (typeof value === 'string' && !isNaN(parseFloat(value.replace(/,/g, ''))))) {
      const numericValue = typeof value === 'number' ? value : parseFloat(value.replace(/,/g, ''));

      // Check if the number has decimal places
      const hasDecimals = numericValue % 1 !== 0;

      if (hasDecimals) {
        return numericValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      } else {
        return numericValue.toLocaleString('en-US');
      }
    }

    return value;
  };

  // Helper function to render dash or spinner
  const renderDashOrSpinner = () => {
    return loading ? <div className="spinner"></div> : <span className="dash">-</span>;
  };

  /** Match InvoiceDashboard / admin product list: integration SKUs stay under API Integration even with zero invoices. */
  const catalogProductHasIntegrationParent = product =>
    product && Array.isArray(product.categoryParent) && product.categoryParent.includes('integration');

  const productsCatalogIntegration = (products || []).filter(catalogProductHasIntegrationParent);
  const productsCatalogNonIntegration = (products || []).filter(p => !catalogProductHasIntegrationParent(p));
  // console.log(data, 2);
  return (
    <table className="products-table">
      <thead>
        <tr>
          <th rowSpan="2">All Products</th>
          <th colSpan="5">Total Amount</th>
          <th colSpan="6">Total Charged Inventories</th>
        </tr>
        <tr>
          <th>Billed</th>
          <th>Collected</th>
          <th>P-INV Created</th>
          <th>Total P-INV Paid</th>
          <th>Total No. of INV & OR Completed</th>
          <th>Host manage</th>
          <th>Host Connect</th>
          <th>DORA</th>
          <th>Subscription Add On</th>
        </tr>
      </thead>
      <tbody>
        {' '}
        <tr>
          <td className="category clickable" onClick={() => toggleSection('subscription')} style={{ cursor: 'pointer' }}>
            {expandedSections.subscription ? '▼' : '▶'} 1. Subscription
          </td>
          <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total Amount Billed')}</td>
          <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total Amount Collected')}</td>
          <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('P-INV Created')}</td>
          <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total of P-INV Paid')}</td>
          <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total INV & OR Completed')}</td>
          <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total Host Manage')}</td>
          <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total Host Connect')}</td>
          <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total DORA')}</td>
          <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total Subscription Add On')}</td>
        </tr>
        {expandedSections.subscription && (
          <>
            {' '}
            <tr className="subcategory-row">
              <td className="subcategory">Monthly</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Monthly Total Billed')}</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Monthly Total Collected')}</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total Monthly Subscription Billed')}</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Monthly P-INV Paid')}</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Monthly Total INV & OR Completed')}</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Monthly Host Manage')}</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Monthly Host Connect')}</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Monthly DORA')}</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Monthly Subscription Add On')}</td>
            </tr>{' '}
            <tr className="subcategory-row">
              <td className="subcategory">Yearly</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Yearly Total Billed')}</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Yearly Total Collected')}</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total Yearly Subscription Billed')}</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Yearly P-INV Paid')}</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Yearly Total INV & OR Completed')}</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Yearly Host Manage')}</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Yearly Host Connect')}</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Yearly DORA')}</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Yearly Subscription Add On')}</td>
            </tr>
          </>
        )}{' '}
        <tr>
          <td className="category clickable" onClick={() => toggleSection('apiIntegration')} style={{ cursor: 'pointer' }}>
            {expandedSections.apiIntegration ? '▼' : '▶'} 2. API Integration
          </td>
          <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total API Integration Billed')}</td>
          <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total API Integration Collected')}</td>
          <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total API Integration INV Created')}</td>
          <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total API Integration INV Paid')}</td>
          <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total API Integration INV & OR Completed')}</td>
          <td className={loading ? 'loading-cell' : ''}>{renderDashOrSpinner()}</td>
          <td className={loading ? 'loading-cell' : ''}>{renderDashOrSpinner()}</td>
          <td className={loading ? 'loading-cell' : ''}>{renderDashOrSpinner()}</td>
          <td className={loading ? 'loading-cell' : ''}>{renderDashOrSpinner()}</td>
        </tr>{' '}
        {expandedSections.apiIntegration &&
          productsCatalogIntegration.map((product, index) => {
            const statsName = `${product.name}${INTEGRATION_PRODUCT_STATS_SUFFIX}`;
            return (
              <tr key={`integration-${product._id || index}`} className="subcategory-row">
                <td className="subcategory">{statsName}</td>
                <td className={loading ? 'loading-cell' : ''}>{findValueByTitle(`Total ${statsName} Billed`)}</td>
                <td className={loading ? 'loading-cell' : ''}>{findValueByTitle(`Total ${statsName} Collected`)}</td>
                <td className={loading ? 'loading-cell' : ''}>{findValueByTitle(`Total ${statsName} INV Created`)}</td>
                <td className={loading ? 'loading-cell' : ''}>{findValueByTitle(`Total ${statsName} INV Paid`)}</td>
                <td className={loading ? 'loading-cell' : ''}>{findValueByTitle(`Total ${statsName} INV & OR Completed`)}</td>
                <td className={loading ? 'loading-cell' : ''}>{renderDashOrSpinner()}</td>
                <td className={loading ? 'loading-cell' : ''}>{renderDashOrSpinner()}</td>
                <td className={loading ? 'loading-cell' : ''}>{renderDashOrSpinner()}</td>
                <td className={loading ? 'loading-cell' : ''}>{renderDashOrSpinner()}</td>
              </tr>
            );
          })}
        <tr>
          <td className="category clickable" onClick={() => toggleSection('others')} style={{ cursor: 'pointer' }}>
            {expandedSections.others ? '▼' : '▶'} 3. Others
          </td>
          <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total Other Product Billed')}</td>
          <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total Other Product Collected')}</td>
          <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total Other Product INV Created')}</td>
          <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total Other Product INV Paid')}</td>
          <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total Other Product INV & OR Completed')}</td>
          <td className={loading ? 'loading-cell' : ''}>{renderDashOrSpinner()}</td>
          <td className={loading ? 'loading-cell' : ''}>{renderDashOrSpinner()}</td>
          <td className={loading ? 'loading-cell' : ''}>{renderDashOrSpinner()}</td>
          <td className={loading ? 'loading-cell' : ''}>{renderDashOrSpinner()}</td>
        </tr>{' '}
        {expandedSections.others && (
          <>
            {' '}
            <tr className="subcategory-row">
              <td className="subcategory">HostProtect</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total HostProtect Billed')}</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total HostProtect Collected')}</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total HostProtect INV Created')}</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total HostProtect INV Paid')}</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total HostProtect INV & OR Completed')}</td>
              <td className={loading ? 'loading-cell' : ''}>{findValueByTitle('Total HostProtect Units')}</td>
              <td className={loading ? 'loading-cell' : ''}>{renderDashOrSpinner()}</td>
              <td className={loading ? 'loading-cell' : ''}>{renderDashOrSpinner()}</td>
              <td className={loading ? 'loading-cell' : ''}>{renderDashOrSpinner()}</td>
            </tr>{' '}
            {productsCatalogNonIntegration.map((product, index) => (
              <tr key={index} className="subcategory-row">
                <td className="subcategory">{product.name}</td>
                <td className={loading ? 'loading-cell' : ''}>{findValueByTitle(`Total ${product.name} Billed`)}</td>
                <td className={loading ? 'loading-cell' : ''}>{findValueByTitle(`Total ${product.name} Collected`)}</td>
                <td className={loading ? 'loading-cell' : ''}>{findValueByTitle(`Total ${product.name} INV Created`)}</td>
                <td className={loading ? 'loading-cell' : ''}>{findValueByTitle(`Total ${product.name} INV Paid`)}</td>
                <td className={loading ? 'loading-cell' : ''}>{findValueByTitle(`Total ${product.name} INV & OR Completed`)}</td>
                <td className={loading ? 'loading-cell' : ''}>{renderDashOrSpinner()}</td>
                <td className={loading ? 'loading-cell' : ''}>{renderDashOrSpinner()}</td>
                <td className={loading ? 'loading-cell' : ''}>{renderDashOrSpinner()}</td>
                <td className={loading ? 'loading-cell' : ''}>{renderDashOrSpinner()}</td>
              </tr>
            ))}
          </>
        )}
      </tbody>
    </table>
  );
};

export default ProductsTable;
